import creator from '../helpers/creator'
import { overlayDebugError, overlayDebugGroup, overlayDebugLog, overlayDebugWarn } from '../helpers/overlayDebug'
import Tabs from './Tabs'
import Video from './Video'
import Search from './Search'

const FETCH_BATCH_SIZE = 4

class Fetch {
  constructor() {
    this.initialize()
  }

  initialize() {
    this.bindUI()
    this.bindEvents()
  }

  bindUI() {
    this.ui = {}
    this.ui.$overlayChild = [...document.querySelectorAll('.js-overlay-child')]
    this.items = []
    this.lang = document.documentElement.lang
  }

  bindEvents() {
    window.addEventListener('load', this.onLoad.bind(this))
  }

  onLoad() {
    for (let i = 0; i < this.ui.$overlayChild.length; i++) {
      let url = this.ui.$overlayChild[i].getAttribute('data-url')
      let id = this.ui.$overlayChild[i].getAttribute('data-id')

      this.items.push({
        id,
        url
      })
    }

    overlayDebugLog('Fetch.onLoad', {
      nodes: this.ui.$overlayChild.length,
      items: this.items.map((it) => ({ id: it.id, url: it.url })),
    })
    if (!this.ui.$overlayChild.length) {
      overlayDebugWarn('Aucun .js-overlay-child détecté au chargement.')
    }
    this.fetchData()
  }

  isProtectedResponse(html) {
    return typeof html === 'string' && html.includes('window.location = window.location.origin')
  }

  isErrorResponse(html, response) {
    if (!response.ok) {
      return true
    }
    if (typeof html !== 'string') {
      return true
    }
    const normalized = html.toLowerCase()
    return (
      normalized.includes('503 service unavailable') ||
      normalized.includes('service unavailable') && normalized.includes('apache') ||
      normalized.includes('502 bad gateway') ||
      normalized.includes('504 gateway timeout') ||
      normalized.includes('internal server error') ||
      normalized.includes('page error')
    )
  }

  markOverlayFetchError(overlays, status) {
    for (let i = 0; i < overlays.length; i++) {
      overlays[i].innerHTML = ''
      overlays[i].dataset.fetchStatus = String(status || 'error')
    }
  }

  async fetchRequest(request, myHeaders) {
    const response = await fetch(request.url, { headers: myHeaders })
    overlayDebugLog('Fetch réponse', {
      source: 'Fetch',
      marker: 'FETCH_SOURCE=Fetch',
      ids: request.ids,
      url: request.url,
      ok: response.ok,
      status: response.status,
      redirected: response.redirected,
    })

    const html = await response.text()
    const escapedIds = request.ids.map((id) => String(id).replace(/"/g, '\\"'))
    const selector = escapedIds
      .map((id) => `.js-overlay-child[data-id="${id}"]`)
      .join(', ')
    const overlays = selector ? document.querySelectorAll(selector) : []

    if (this.isErrorResponse(html, response)) {
      overlayDebugWarn('Réponse fetch ignorée (HTTP ou page erreur)', {
        ids: request.ids,
        url: request.url,
        status: response.status,
      })
      this.markOverlayFetchError(overlays, response.status)
      return
    }

    if (this.isProtectedResponse(html)) {
      overlayDebugWarn('Réponse protégée détectée (base-protected).', {
        ids: request.ids,
        url: request.url,
      })
    }

    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    const bodyNode = doc.querySelector('body')
    let content = bodyNode ? bodyNode.innerHTML : ''
    overlayDebugGroup(`HTML injecté [ids=${request.ids.join(',')}]`, () => {
      overlayDebugLog('longueur body', content.length, 'cibles', overlays.length, 'url', request.url)
    })
    if (!content.trim()) {
      overlayDebugWarn('Réponse fetch vide (body vide)', { ids: request.ids, url: request.url })
    }
    if (!overlays.length) {
      overlayDebugWarn('Aucune cible overlay trouvée pour ids/url.', {
        ids: request.ids,
        url: request.url,
      })
    }
    for (let i = 0; i < overlays.length; i++) {
      overlays[i].innerHTML = content
      delete overlays[i].dataset.fetchStatus
    }
    window.tabs = creator('.js-tabs', Tabs)
    window.video = creator('.js-card', Video)
    window.search = new Search()
  }

  async fetchBatch(requests, myHeaders) {
    await Promise.all(
      requests.map(async (request) => {
        try {
          await this.fetchRequest(request, myHeaders)
        } catch (err) {
          overlayDebugError('échec fetch item', { ids: request.ids, url: request.url }, err)
          console.error('Failed to fetch one or more of these URLs:')
          console.error(err)
        }
      })
    )
  }

  async fetchData() {
    // Keep this sequence stable for SPA overlays:
    // collect overlay URLs -> fetch/inject HTML -> always dispatch fetch:done.
    const myHeaders = new Headers()
    myHeaders.append("x-custom-provider", "dior-montaigne")

    const groupedByUrl = this.items.reduce((acc, item) => {
      if (!item || !item.url) {
        return acc
      }
      if (!acc[item.url]) {
        acc[item.url] = []
      }
      if (item.id && !acc[item.url].includes(item.id)) {
        acc[item.url].push(item.id)
      }
      return acc
    }, {})

    const requests = Object.entries(groupedByUrl).map(([url, ids]) => ({ url, ids }))
    overlayDebugLog('Fetch.fetchData groupé par URL', {
      source: 'Fetch',
      marker: 'FETCH_SOURCE=Fetch',
      uniqueUrls: requests.length,
      groups: requests,
    })

    try {
      for (let i = 0; i < requests.length; i += FETCH_BATCH_SIZE) {
        const batch = requests.slice(i, i + FETCH_BATCH_SIZE)
        await this.fetchBatch(batch, myHeaders)
      }
    } catch (err) {
      overlayDebugError('Fetch.fetchData global', err)
      console.error(err)
    } finally {
      const event = new CustomEvent("fetch:done")
      setTimeout(() => {
        window.dispatchEvent(event)
      }, 100)
    }
  }
}

export default Fetch
